/**
 * 🚗 ENHANCED DRIVER CARD - Driver approval card with document image display
 */

import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  Chip,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Visibility,
  Schedule,
  Person,
  Badge,
  Assignment,
  MoreVert,
  ZoomIn,
  Phone,
  Email,
} from '@mui/icons-material';

import { DocumentViewer } from '@/components/documents/DocumentViewer';
import { PhotoGallery, type GalleryImage } from '@/components/documents/PhotoGallery';
import { DocumentCompletionStatus } from '@/components/documents/ImageStatus';
import { VerificationStatusBadge, getVerificationStatusFromUser } from '@/components/users/VerificationStatusBadge';
import type { AdminDriver } from '@/services/adminApiService';

interface EnhancedDriverCardProps {
  driver: AdminDriver;
  onApprove: (id: string, notes: string) => void;
  onReject: (id: string, reason: string) => void;
  onViewProfile?: (id: string) => void;
}

export const EnhancedDriverCard: React.FC<EnhancedDriverCardProps> = ({
  driver,
  onApprove,
  onReject,
  onViewProfile
}) => {
  const [fullDocumentsOpen, setFullDocumentsOpen] = useState(false);
  const [actionDialog, setActionDialog] = useState<{
    open: boolean;
    action: 'approve' | 'reject' | null;
    notes: string;
  }>({
    open: false,
    action: null,
    notes: ''
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'success';
      case 'pending_review': return 'warning';
      case 'rejected': return 'error';
      case 'draft': return 'info';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending_review': return 'Pending Review';
      case 'approved': return 'Approved';
      case 'rejected': return 'Rejected';
      case 'draft': return 'Draft';
      default: return status;
    }
  };

  // Convert driver data to gallery images
  const galleryImages: GalleryImage[] = [
    {
      url: driver.identity.identityPhotoUrl,
      title: 'Identity Document',
      documentType: 'identity',
      status: driver.identity.identityPhotoUrl ? 'pending' : undefined
    },
    {
      url: driver.identity.selfiePhotoUrl,
      title: 'Driver Selfie',
      documentType: 'selfie',
      status: driver.identity.selfiePhotoUrl ? 'pending' : undefined
    },
    {
      url: driver.license.frontPhotoUrl,
      title: 'License Front',
      documentType: 'license_front',
      status: driver.license.frontPhotoUrl ? 'pending' : undefined
    },
    {
      url: driver.license.backPhotoUrl,
      title: 'License Back',
      documentType: 'license_back',
      status: driver.license.backPhotoUrl ? 'pending' : undefined
    }
  ];

  const documentsStatus = {
    identity: {
      hasImage: !!driver.identity.identityPhotoUrl,
      status: driver.identity.identityPhotoUrl ? 'pending' : 'not_uploaded'
    },
    selfie: { 
      hasImage: !!driver.identity.selfiePhotoUrl, 
      status: driver.identity.selfiePhotoUrl ? 'pending' : 'not_uploaded' 
    },
    license_front: { 
      hasImage: !!driver.license.frontPhotoUrl, 
      status: driver.license.frontPhotoUrl ? 'pending' : 'not_uploaded' 
    },
    license_back: { 
      hasImage: !!driver.license.backPhotoUrl, 
      status: driver.license.backPhotoUrl ? 'pending' : 'not_uploaded' 
    }
  };

  const hasAnyImages = galleryImages.some(img => img.url);
  const uploadedCount = galleryImages.filter(img => img.url).length;

  const handleActionConfirm = () => {
    if (actionDialog.action === 'approve') {
      onApprove(driver.id, actionDialog.notes);
    } else if (actionDialog.action === 'reject') {
      onReject(driver.id, actionDialog.notes);
    }
    setActionDialog({ open: false, action: null, notes: '' });
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        {/* Header with driver info and status */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Avatar
              src={driver.identity.selfiePhotoUrl || driver.identity.identityPhotoUrl}
              sx={{ width: 64, height: 64, border: '2px solid', borderColor: 'divider' }}
            >
              {driver.userName.charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                {driver.userName}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Phone sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {driver.userPhone || 'No phone'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <Email sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {driver.userEmail || 'No email'}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" display="block">
                NIN: {driver.identity.nin.replace(/(.{4})(.*)(.{4})/, '$1****$3')} • License: {driver.license.number}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                License Expires: {new Date(driver.license.expiryDate).toLocaleDateString()}
              </Typography>
            </Box>
          </Box>
          <Chip
            label={getStatusLabel(driver.status)}
            color={getStatusColor(driver.status) as any}
            size="small"
          />
        </Box>

        {/* Progress and document status */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Application Progress: {driver.completionPercentage}%
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={driver.completionPercentage} 
                sx={{ height: 6, borderRadius: 3 }}
                color={driver.completionPercentage === 100 ? 'success' : 'primary'}
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              {uploadedCount}/4 documents
            </Typography>
          </Box>

          <DocumentCompletionStatus documents={documentsStatus} showDetails={false} />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Document previews */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Submitted Documents
            </Typography>
            {hasAnyImages && (
              <Button
                size="small"
                startIcon={<ZoomIn />}
                onClick={() => setFullDocumentsOpen(true)}
              >
                View Full Documents
              </Button>
            )}
          </Box>

          {hasAnyImages ? (
            <Grid container spacing={1}>
              <Grid item xs={3}>
                <DocumentViewer
                  imageUrl={driver.identity.identityPhotoUrl}
                  title="Identity Document"
                  documentType="identity"
                  width={80}
                  height={60}
                  showStatus={false}
                  onViewFull={() => setFullDocumentsOpen(true)}
                />
              </Grid>
              <Grid item xs={3}>
                <DocumentViewer
                  imageUrl={driver.identity.selfiePhotoUrl}
                  title="Driver Selfie"
                  documentType="selfie"
                  width={80}
                  height={60}
                  showStatus={false}
                  onViewFull={() => setFullDocumentsOpen(true)}
                />
              </Grid>
              <Grid item xs={3}>
                <DocumentViewer
                  imageUrl={driver.license.frontPhotoUrl}
                  title="License Front"
                  documentType="license_front"
                  width={80}
                  height={60}
                  showStatus={false}
                  onViewFull={() => setFullDocumentsOpen(true)}
                />
              </Grid>
              <Grid item xs={3}>
                <DocumentViewer
                  imageUrl={driver.license.backPhotoUrl}
                  title="License Back"
                  documentType="license_back"
                  width={80}
                  height={60}
                  showStatus={false}
                  onViewFull={() => setFullDocumentsOpen(true)}
                />
              </Grid>
            </Grid>
          ) : (
            <Box sx={{ 
              textAlign: 'center', 
              py: 3, 
              bgcolor: 'grey.50', 
              borderRadius: 1,
              border: '2px dashed',
              borderColor: 'grey.300'
            }}>
              <Assignment sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                No documents uploaded yet
              </Typography>
            </Box>
          )}
        </Box>

        {/* Action buttons */}
        {driver.canApprove && driver.status === 'pending_review' && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              color="success"
              size="small"
              startIcon={<CheckCircle />}
              onClick={() => setActionDialog({ open: true, action: 'approve', notes: '' })}
              disabled={driver.completionPercentage < 100}
            >
              Approve
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<Cancel />}
              onClick={() => setActionDialog({ open: true, action: 'reject', notes: '' })}
            >
              Reject
            </Button>
            {onViewProfile && (
              <IconButton size="small" onClick={() => onViewProfile(driver.id)}>
                <Visibility />
              </IconButton>
            )}
          </Box>
        )}

        {driver.submittedAt && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Submitted: {new Date(driver.submittedAt).toLocaleDateString()}
            {driver.reviewedAt && ` • Reviewed: ${new Date(driver.reviewedAt).toLocaleDateString()}`}
          </Typography>
        )}
      </CardContent>

      {/* Full Documents Modal */}
      <Dialog
        open={fullDocumentsOpen}
        onClose={() => setFullDocumentsOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Driver Documents - {driver.userName}
        </DialogTitle>
        <DialogContent>
          <PhotoGallery
            images={galleryImages}
            driverName={driver.userName}
            onApprove={driver.canApprove ? () => {
              setFullDocumentsOpen(false);
              setActionDialog({ open: true, action: 'approve', notes: '' });
            } : undefined}
            onReject={driver.canApprove ? () => {
              setFullDocumentsOpen(false);
              setActionDialog({ open: true, action: 'reject', notes: '' });
            } : undefined}
            showActions={driver.canApprove && driver.status === 'pending_review'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFullDocumentsOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action Confirmation Dialog */}
      <Dialog open={actionDialog.open} onClose={() => setActionDialog({ open: false, action: null, notes: '' })}>
        <DialogTitle>
          {actionDialog.action === 'approve' ? 'Approve' : 'Reject'} Driver Application
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {actionDialog.action === 'approve' 
              ? `Are you sure you want to approve ${driver.userName}'s driver application?`
              : `Please provide a reason for rejecting ${driver.userName}'s driver application.`
            }
          </Typography>
          <textarea
            placeholder={actionDialog.action === 'approve' ? 'Optional notes...' : 'Rejection reason...'}
            value={actionDialog.notes}
            onChange={(e) => setActionDialog(prev => ({ ...prev, notes: e.target.value }))}
            style={{
              width: '100%',
              minHeight: '80px',
              padding: '8px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontFamily: 'inherit'
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog({ open: false, action: null, notes: '' })}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={actionDialog.action === 'approve' ? 'success' : 'error'}
            onClick={handleActionConfirm}
            disabled={actionDialog.action === 'reject' && !actionDialog.notes.trim()}
          >
            {actionDialog.action === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};